import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { endOfWeek, format } from 'date-fns';
import api from '../config/configApi';

// 5 minutos: essa contagem não precisa de tempo real (é "não esqueça a ligação
// desta semana", não um chat) - um intervalo curto só geraria requests inúteis
// pra maioria das sessões, que ficam abertas o dia todo. Pausa quando a aba
// não está visível e revalida na volta do foco, em vez de continuar batendo
// no servidor com a aba em segundo plano.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// `ativo: false` não consulta o servidor (quem não tem acesso às tarefas não vê o sino).
export default function useTarefasContatoPendentesCount({ ativo = true } = {}) {
  const [total, setTotal] = useState(0);
  const [semanaAtual, setSemanaAtual] = useState(null);
  const unidadeIdRef = useRef(undefined);

  const buscarContagem = useCallback(async () => {
    try {
      if (unidadeIdRef.current === undefined) {
        const { data } = await api.get('/profile/view-profile');
        unidadeIdRef.current = data?.user?.unidadeId ?? null;
      }

      if (!unidadeIdRef.current) {
        setTotal(0);
        return;
      }

      // Sem limite inferior de data de propósito: "pendências desta semana +
      // atrasadas" é o mesmo conjunto que "tudo que está PENDENTE com
      // vencimento até domingo desta semana" (atrasada = dataPrevista < hoje,
      // que já é subconjunto disso) - uma única consulta cobre os dois casos
      // sem contar nada em duplicidade.
      const dataPrevistaAte = format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const unidadeId = unidadeIdRef.current;

      setSemanaAtual({ unidadeId, dataPrevistaAte });

      const { data } = await api.get('/tarefas-contato/contagem-pendentes', {
        params: { unidadeId, dataPrevistaAte },
      });
      setTotal(data?.total || 0);
    } catch (error) {
      // Badge não é crítico - uma falha aqui não deve travar navegação nem poluir a UI.
      setTotal(0);
    }
  }, []);

  useEffect(() => {
    if (!ativo) return undefined;
    buscarContagem();

    const interval = setInterval(() => {
      if (!document.hidden) buscarContagem();
    }, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (!document.hidden) buscarContagem();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [ativo, buscarContagem]);

  return { total, semanaAtual };
}
